// 사이드바 — 바닐라 aside.sidebar 완성 이식 (Phase 3-B):
// 접기/리사이저 · 새 채팅 · 문서함 · 도구(…시장 이슈 · DCF 워크스페이스) · 예정 서비스(리포트 베타 포함) ·
// 대화 그룹(만들기·접기·이름 변경·삭제·그룹에서 새 채팅) · 대화 목록(고정 정렬) ·
// ⋯ 메뉴(이름 변경·고정·그룹 이동·링크 복사·삭제) · 사용자 메뉴(로그아웃).
// 마크업 클래스는 바닐라와 동일 (chat.css = components.css 이식본).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GRAPH_CHAT_ENABLED } from "../../lib/graphChat";
import { useLabState } from "../../lib/labState";
import { sessionDateLabel } from "../../lib/chatText";
import { useAuth } from "../../stores/auth";
import { isEnterpriseUser } from "../../lib/enterprise";
import {
  collapsedGroups,
  groupNames,
  persistCollapsedGroups,
  sortedSessions,
  useChat,
  type VaSession,
} from "../../stores/chat";
import ConvMenu, { type MenuItem } from "./ConvMenu";
import { REPORT_ENABLED } from "../../lib/reportMeta";
import { RE_ENABLED } from "../../lib/realEstate";
import { RCPS_ENABLED } from "../../lib/rcps/enabled";
import { Layers3 } from "lucide-react";
import SessionMenu from "./SessionMenu";
import { BrandMark } from "../../ui/BrandMark";

interface Props {
  sessions: VaSession[];
  activeId: string | null;
  /** 지금 응답을 작성 중인 대화들 — 대화 전환이 열려 동시에 여럿일 수 있다 */
  writingIds?: string[];
  /** 안 보는 사이 답변이 끝난 대화 — 파란 불릿 */
  doneIds?: string[];
  busy: boolean;
  onNew: () => void;
  /** 그룹 행의 ✎ — 이 그룹 소속으로 새 채팅 시작 */
  onNewInGroup: (group: string) => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onOpenLibrary: () => void;
  libraryOpen: boolean;
  /** 아이콘 레일 상태 — 레일에서는 대화 목록을 팝오버로 연다.
   *  (이름이 railMode 인 이유: 이 파일의 collapsed 는 '접힌 그룹 집합'이다) */
  railMode: boolean;
  onCollapse: () => void;
  /** 레일에서 다시 펼치기 — 토글이 접기만 하면 레일에 갇힌다 */
  onExpand: () => void;
  onResizingChange: (on: boolean) => void;
  /** 활성 대화의 이름이 바뀌면 상단 제목도 동기화 */
  onRenamed: (id: string, title: string) => void;
  /** 사이드바 안에서 자체 navigate 로 섹션을 옮긴 직후 — 모바일 오버레이를 닫는 용도.
   *  같은 경로를 다시 눌러도 pathname 이 안 바뀌어 셸의 경로 감시로는 못 잡는다. */
  onNavigated?: () => void;
}

/** 불릿에 붙일 설명 — 상태가 없으면 undefined (툴팁·읽어주기 모두 생략) */
function statusLabel(id: string, writingIds?: string[], doneIds?: string[]) {
  if ((writingIds || []).includes(id)) return "응답 작성 중";
  if ((doneIds || []).includes(id)) return "답변 완료 — 아직 열어보지 않음";
  return undefined;
}

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};
const ICON_PIN = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M12 17v5" />
    <path d="M9 2h6l-1 6 3 3v2H7v-2l3-3z" />
  </svg>
);
const ICON_DOTS = (
  <svg viewBox="0 0 24 24" fill="none" stroke="none">
    <circle cx="12" cy="5" r="1.4" fill="currentColor" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    <circle cx="12" cy="19" r="1.4" fill="currentColor" />
  </svg>
);
const ICON_RENAME = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);
const ICON_TRASH = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
  </svg>
);
const ICON_FOLDER = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);
const ICON_FOLDER_OPEN = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M6 14l1.5-4.5A2 2 0 0 1 9.4 8H21a1 1 0 0 1 1 1.3L20.2 16a2 2 0 0 1-1.9 1.4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h7" />
  </svg>
);
const ICON_NEWCHAT = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.4 2.6a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);
const ICON_CHEV_D = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const ICON_CHEV_R = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);
const ICON_PLUS = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);
/* 문서함 — 그룹(폴더)과 같은 모양이면 구분이 안 된다(실제로 같은 path 였다).
   문서함은 '만들어진 문서'가 쌓이는 곳이므로 문서 더미로 (lucide files). */
const ICON_LIB = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M15 2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7z" />
    <path d="M15 2v5h5" />
    <path d="M20 9v11a2 2 0 0 1-2 2H8" />
  </svg>
);
const ICON_CHATS = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const ICON_PANEL_OPEN = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d="m14 9 3 3-3 3" />
  </svg>
);
const ICON_PANEL_CLOSE = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d="m16 15-3-3 3-3" />
  </svg>
);
const ICON_GAUGE = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="m12 14 4-4" />
    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
  </svg>
);
const ICON_GEAR = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const ICON_SHIELD = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ICON_LOGOUT = (
  <svg viewBox="0 0 24 24" {...stroke}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

type MenuState =
  | { kind: "conv"; id: string; anchor: HTMLElement }
  | { kind: "group"; name: string; anchor: HTMLElement }
  | { kind: "user"; anchor: HTMLElement };

const SIDE_MIN = 210, SIDE_MAX = 460, SIDE_DEF = 292;

export default function Sidebar({
  sessions,
  activeId,
  writingIds,
  doneIds,
  busy: _busy,   // 가드 제거로 미사용 — 프롭 호환 유지
  onNew,
  onNewInGroup,
  onOpen,
  onDelete,
  onOpenLibrary,
  libraryOpen,
  railMode,
  onCollapse,
  onExpand,
  onResizingChange,
  onRenamed,
  onNavigated,
}: Props) {
  const navigate = useNavigate();
  /** 섹션 이동 — 이동 후 모바일 오버레이 닫기까지 한 동작 */
  const goSection = (path: string) => {
    navigate(path);
    onNavigated?.();
  };
  const { pathname } = useLocation();
  // 버핏·유사기업 조회의 백그라운드 작업 상태 — 채팅처럼 하트비트(파랑 펄스)/완료(파란 점).
  // 완료 점은 result 존재가 아니라 done 플래그를 본다 — result 는 영구 보존이라
  // 그걸 보면 점이 영원히 남는다. done 은 완료 시 켜지고, 페이지를 열어 보면
  // (각 페이지의 effect 가) 꺼진다 — 채팅의 markDone/clearDone 과 같은 의미.
  const [bfRunning] = useLabState("bf.running", false);
  const [bfDone] = useLabState("bf.done", false);
  const [paRunning] = useLabState("pa.running", false);
  const [paDone] = useLabState("pa.done", false);
  const [itRunning] = useLabState("it.running", false);
  const [itDone] = useLabState("it.done", false);
  const peerRunning = paRunning || itRunning;
  const bfDot = bfRunning ? "writing" : bfDone ? "done" : "";
  const peerDot = peerRunning ? "writing" : (paDone || itDone) ? "done" : "";
  const user = useAuth((s) => s.user);
  // DCF 워크스페이스 · 금융상품 평가는 딥서치(엔터프라이즈) 계정 전용 — 구글(베타) 로그인에는 메뉴를 숨긴다
  const enterprise = isEnterpriseUser(user);
  const userKey = useChat((s) => s.userKey);
  const account = String(user?.username || user?.["email"] || user?.name || "사용자");
  const shortName = account.split("@")[0];

  const asideRef = useRef<HTMLElement>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  /** 레일에서 대화 목록을 여는 팝오버. 펼침으로 돌아가면 닫는다 */
  const [railChats, setRailChats] = useState(false);
  /** 레일 그룹 팝오버 — 그룹과 그 안의 대화를 뎁스 그대로 보여준다 */
  const [railGroups, setRailGroups] = useState(false);
  const [railTools, setRailTools] = useState(false);
  useEffect(() => {
    if (!railMode) {
      setRailChats(false);
      setRailGroups(false);
    }
  }, [railMode]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [collapseTick, setCollapseTick] = useState(0); // 접힘 상태(localStorage) 리렌더
  const [sectionCollapsed, setSectionCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("va_section_collapsed::밸류에이션 에이전트") === "1";
    } catch {
      return false;
    }
  });
  const [plannedOpen, setPlannedOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem("va_planned_open") === "1";
    } catch {
      return false;
    }
  });

  // 레일 팝오버 — 바깥을 누르거나 ESC 면 닫는다 (메뉴들과 같은 규칙)
  useEffect(() => {
    if (!railChats && !railGroups && !railTools) return;
    const close = () => {
      setRailChats(false);
      setRailGroups(false);
      setRailTools(false);
    };
    const onDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node) || !(e.target as Element).closest?.(".rail-chats")) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [railChats, railGroups, railTools]);

  // 저장된 사이드바 너비 복원 (va_side_w — .shell 의 CSS 변수)
  useEffect(() => {
    try {
      const sw = parseInt(localStorage.getItem("va_side_w") || String(SIDE_DEF), 10);
      const shell = asideRef.current?.closest(".shell") as HTMLElement | null;
      if (sw && shell)
        shell.style.setProperty("--side-w", Math.max(SIDE_MIN, Math.min(SIDE_MAX, sw)) + "px");
    } catch {
      /* noop */
    }
  }, []);

  const setWidth = (px: number) => {
    const w = Math.max(SIDE_MIN, Math.min(SIDE_MAX, px));
    const shell = asideRef.current?.closest(".shell") as HTMLElement | null;
    if (shell) shell.style.setProperty("--side-w", w + "px");
    try {
      localStorage.setItem("va_side_w", String(w));
    } catch {
      /* noop */
    }
  };
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    onResizingChange(true);
    const move = (ev: MouseEvent) => setWidth(ev.clientX);
    const up = () => {
      onResizingChange(false);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const closeMenu = () => setMenu(null);

  const collapsed = collapsedGroups(userKey);
  const toggleCollapsed = (name: string) => {
    const set = collapsedGroups(userKey);
    if (set.has(name)) set.delete(name);
    else set.add(name);
    persistCollapsedGroups(userKey, set);
    setCollapseTick(collapseTick + 1);
  };

  const ordered = sortedSessions(sessions);
  const grouped = new Map<string, VaSession[]>();
  const ungrouped: VaSession[] = [];
  ordered.forEach((s) => {
    if (s.group) {
      if (!grouped.has(s.group)) grouped.set(s.group, []);
      (grouped.get(s.group) as VaSession[]).push(s);
    } else ungrouped.push(s);
  });
  const allGroups = groupNames(userKey, sessions);
  const groupsOpen = !collapsed.has("__groups__");
  const chatsOpen = !collapsed.has("__chats__");

  const store = () => useChat.getState();

  const finishRename = (id: string, value: string, save: boolean) => {
    setRenamingId(null);
    if (!save) return;
    const title = (value || "").trim().slice(0, 60);
    if (!title) return;
    store().renameSession(id, title);
    onRenamed(id, title);
  };

  // ── 행 렌더 ──
  const row = (s: VaSession, indent: boolean) => {
    const date = sessionDateLabel(s);
    if (renamingId === s.id) {
      return (
        <div
          key={s.id}
          className={`conversation${s.id === activeId ? " active" : ""}${indent ? " in-group" : ""}`}
          data-row={s.id}
        >
          <input
            className="conversation-rename"
            defaultValue={s.title}
            maxLength={60}
            aria-label="대화 이름"
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                finishRename(s.id, e.currentTarget.value, true);
              } else if (e.key === "Escape") {
                e.preventDefault();
                finishRename(s.id, "", false);
              }
            }}
            onBlur={(e) => finishRename(s.id, e.currentTarget.value, true)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      );
    }
    return (
      <div
        key={s.id}
        className={`conversation${s.id === activeId ? " active" : ""}${s.pinned ? " pinned" : ""}${indent ? " in-group" : ""}`}
        data-row={s.id}
      >
        <span
          className={`conv-status${
            (writingIds || []).includes(s.id)
              ? " writing"
              : (doneIds || []).includes(s.id)
                ? " done"
                : s.id === activeId
                  ? " current"
                  : ""
          }`}
          title={statusLabel(s.id, writingIds, doneIds)}
          aria-label={statusLabel(s.id, writingIds, doneIds)}
          role={statusLabel(s.id, writingIds, doneIds) ? "status" : undefined}
        />
        <button
          className="conversation-open"
          type="button"
          title={date}
          onClick={() => onOpen(s.id)}
        >
          {s.title}
        </button>
        <span className="conversation-pin" title="고정됨" aria-label="고정됨">
          {ICON_PIN}
        </span>
        <button
          className="conv-menu-btn"
          type="button"
          title="대화 메뉴"
          aria-label="대화 메뉴"
          aria-haspopup="menu"
          aria-expanded={menu?.kind === "conv" && menu.id === s.id}
          onClick={(e) => {
            e.stopPropagation();
            const anchor = e.currentTarget;
            setMenu((m) =>
              m && m.kind === "conv" && m.id === s.id ? null : { kind: "conv", id: s.id, anchor },
            );
          }}
        >
          {ICON_DOTS}
        </button>
      </div>
    );
  };

  // ── 메뉴 items ──
  const menuNode = (() => {
    if (!menu) return null;
    if (menu.kind === "conv") {
      return (
        <SessionMenu
          id={menu.id}
          anchor={menu.anchor}
          onClose={closeMenu}
          onRenameStart={(id) => setRenamingId(id)}
          onDelete={onDelete}
        />
      );
    }
    if (menu.kind === "group") {
      const items: MenuItem[] = [
        { key: "rename", label: "이름 변경", icon: ICON_RENAME },
        "sep",
        { key: "delete", label: "그룹 삭제", icon: ICON_TRASH, danger: true },
      ];
      return (
        <ConvMenu
          anchor={menu.anchor}
          items={items}
          onPick={(k) => {
            closeMenu();
            const name = menu.name;
            const members = sessions.filter((s) => s.group === name);
            if (k === "rename") {
              const next = (window.prompt("그룹 이름 변경", name) || "").trim().slice(0, 30);
              if (next && next !== name) store().renameGroup(name, next);
            } else if (k === "delete") {
              if (
                window.confirm(
                  `'${name}' 그룹을 삭제할까요? 대화 ${members.length}개는 '채팅'으로 이동합니다.`,
                )
              )
                store().deleteGroup(name);
            }
          }}
          onClose={closeMenu}
        />
      );
    }
    // user menu — 사용량·설정은 준비 중이라 딤드
    const isAdmin = !!useAuth.getState().user?.is_admin;
    const items: MenuItem[] = [
      { key: "usage", label: "남은 사용량", icon: ICON_GAUGE, disabled: true },
      { key: "settings", label: "설정", icon: ICON_GEAR, disabled: true },
      // 관리자에게만 보인다. 숨김은 편의일 뿐 보안이 아니다 — 서버가 다시 본다.
      ...(isAdmin
        ? ([{ key: "admin", label: "관리자 대시보드", icon: ICON_SHIELD }] as MenuItem[])
        : []),
      "sep",
      { key: "logout", label: "로그아웃", icon: ICON_LOGOUT },
    ];
    return (
      <ConvMenu
        anchor={menu.anchor}
        items={items}
        header={account}
        place="above"
        onPick={(k) => {
          closeMenu();
          if (k === "logout") void useAuth.getState().logout();
          else if (k === "admin") navigate("/admin");
        }}
        onClose={closeMenu}
      />
    );
  })();

  const kicker = (name: string, label: string, open: boolean, extra?: ReactNode) => (
    <div className="list-kicker">
      <button
        className="kicker-label"
        type="button"
        aria-expanded={open}
        onClick={() => toggleCollapsed(name)}
      >
        <span>{label}</span>
        <span className="kicker-chevron">{open ? ICON_CHEV_D : ICON_CHEV_R}</span>
      </button>
      {extra}
    </div>
  );

  return (
    <aside className="sidebar" aria-label="대화 사이드패널" ref={asideRef}>
      {/* 접기/펼치기 — 레일에서는 로고 자리에 겹쳐 두고 호버할 때만 드러난다.
          레일에서 접기만 하면 다시 펼칠 방법이 없다(실제로 갇혔다) → 방향을 뒤집는다. */}
      <button
        className="side-toggle"
        type="button"
        title={railMode ? "사이드패널 펼치기" : "사이드패널 접기"}
        aria-label={railMode ? "사이드패널 펼치기" : "사이드패널 접기"}
        onClick={railMode ? onExpand : onCollapse}
      >
        {railMode ? ICON_PANEL_OPEN : ICON_PANEL_CLOSE}
      </button>
      <div className="side-resizer" title="너비 조절" onMouseDown={startResize} onDoubleClick={() => setWidth(SIDE_DEF)} />

      <a
        className="brand"
        href="/"
        title="새 채팅"
        onClick={(e) => {
          e.preventDefault();
          onNew();   // 로고 클릭 = 새 채팅 (사용자 피드백)
        }}
      >
        <BrandMark className="brand-mark" />
        <strong>Valuation</strong>
      </a>

      <nav className="side-nav">
        <button className="side-item" type="button" data-tip="새 채팅" aria-label="새 채팅" onClick={onNew}>
          {ICON_NEWCHAT}
          <span>새 채팅</span>
        </button>
        <button
          className={`side-item${libraryOpen ? " active" : ""}`}
          type="button"
          data-tip="문서함"
          aria-label="문서함 — 생성한 보고서·엑셀 모아보기"
          onClick={onOpenLibrary}
        >
          {ICON_LIB}
          <span>문서함</span>
        </button>
        {/* 레일 — 밸류에이션 에이전트 섹션은 아이콘 하나로 접고 팝오버에서 고른다 */}
        {railMode && (
          <div className="rail-chats">
            <button
              className={`side-item${railTools ? " active" : ""}`}
              type="button"
              data-tip="밸류에이션 에이전트"
              aria-label="밸류에이션 에이전트 도구"
              aria-haspopup="menu"
              aria-expanded={railTools}
              onClick={() => {
                setRailChats(false);
                setRailGroups(false);
                setRailTools((o) => !o);
              }}
            >
              {/* 레일 항목은 이웃과 같은 Lucide 라인 아이콘(layers) — 정본 심볼은
                  3겹 반투명이라 16px 에서 뭉개진다. 컬러 심볼은 브랜드 헤더에서만. */}
              <svg viewBox="0 0 24 24" {...stroke}>
                <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
                <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
                <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
              </svg>
              <span>밸류에이션 에이전트</span>
            </button>
            {railTools && (
              <div className="rail-pop" role="menu" aria-label="밸류에이션 에이전트">
                <div className="rail-pop-head">밸류에이션 에이전트</div>
                {([
                  ["/market", "시장 이슈",
                    <svg key="i" viewBox="0 0 24 24" {...stroke}><path d="M3 17l5.5-6 3.5 3.5L20.5 6" /><path d="M15.5 6h5v5" /></svg>],
                  ["/peer", "유사기업 조회",
                    <svg key="i" viewBox="0 0 24 24" {...stroke}><circle cx="9" cy="7" r="3" /><path d="M2 21v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v2" /><circle cx="17.5" cy="7.5" r="2.5" /><path d="M22 21v-1.5a4 4 0 0 0-3-3.87" /></svg>],
                  ...(RCPS_ENABLED && enterprise ? [["/rcps", "금융상품 평가", <Layers3 key="i" />]] : []),
                  ...(enterprise ? [["/dcf", "DCF 워크스페이스",
                    <svg key="i" viewBox="0 0 24 24" {...stroke}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M10 4v4" /><path d="M2 8h20" /><path d="M6 4v4" /></svg>]] : []),
                  ["/buffett", "버핏스코어",
                    <svg key="i" viewBox="0 0 24 24" {...stroke}><path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" /><circle cx="12" cy="8" r="6" /></svg>],
                ] as [string, string, ReactNode][]).map(([path, label, icon]) => (
                  <button
                    key={path}
                    className="rail-pop-item rail-pop-tool"
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setRailTools(false);
                      goSection(path);
                    }}
                  >
                    {icon}
                    {label}
                    {path === "/rcps" && <span className="badge-soon">베타</span>}
                  </button>
                ))}
                <div className="rail-pop-group"><span>예정 서비스</span></div>
                {REPORT_ENABLED && (
                  <button className="rail-pop-item rail-pop-tool in-group" type="button"
                    onClick={() => { setRailTools(false); goSection("/report"); }}>
                    리포트
                    <span className="badge-soon">베타</span>
                  </button>
                )}
                {GRAPH_CHAT_ENABLED && (
                  <button className="rail-pop-item rail-pop-tool in-group" type="button"
                    onClick={() => goSection("/graph-chat")}>
                    Graph DB 챗
                    <span className="badge-soon">베타</span>
                  </button>
                )}
                {RE_ENABLED && (
                  <button className="rail-pop-item rail-pop-tool in-group" type="button"
                    onClick={() => goSection("/re")}>
                    부동산 개발·매매사례
                    <span className="badge-soon">베타</span>
                  </button>
                )}
                {(RCPS_ENABLED ? ["투자 백테스팅"] : ["투자 백테스팅", "RCPS", "스톡옵션"]).map((label) => (
                  <button key={label} className="rail-pop-item rail-pop-tool in-group" type="button" disabled>
                    {label}
                    <span className="badge-soon">예정</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/* 레일 그룹 — 펼친 사이드바와 같은 순서(그룹 위, 채팅 아래). 팝오버는 그룹 > 대화 뎁스 유지 */}
        {railMode && allGroups.length > 0 && (
          <div className="rail-chats">
            <button
              className={`side-item${railGroups ? " active" : ""}`}
              type="button"
              data-tip="그룹"
              aria-label="그룹"
              aria-haspopup="menu"
              aria-expanded={railGroups}
              onClick={() => {
                setRailChats(false);
                setRailGroups((o) => !o);
              }}
            >
              {ICON_FOLDER}
              <span>그룹</span>
            </button>
            {railGroups && (
              <div className="rail-pop" role="menu" aria-label="그룹">
                <div className="rail-pop-head">그룹</div>
                {allGroups.map((g) => {
                  const items = grouped.get(g) || [];
                  return (
                    <div key={g}>
                      <div className="rail-pop-group">
                        {ICON_FOLDER}
                        <span>{g}</span>
                        <span className="rail-pop-count">{items.length}</span>
                      </div>
                      {items.length === 0 ? (
                        <div className="rail-pop-empty in-group">비어 있어요</div>
                      ) : (
                        items.map((sx) => (
                          <button
                            key={sx.id}
                            className={`rail-pop-item in-group${sx.id === activeId ? " active" : ""}`}
                            type="button"
                            role="menuitem"
                            title={sx.title || "새 채팅"}
                            onClick={() => {
                              setRailGroups(false);
                              onOpen(sx.id);
                            }}
                          >
                            {(writingIds || []).includes(sx.id) && <span className="conv-writing" aria-label="응답 작성 중" />}
                            {sx.title || "새 채팅"}
                          </button>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* 레일에서만 — 대화 목록을 팝오버로 연다. 펼침 상태에서는 목록이 그대로 보이므로 없다 */}
        {railMode && (
          <div className="rail-chats">
            <button
              className={`side-item${railChats ? " active" : ""}`}
              type="button"
              data-tip="채팅"
              aria-label="대화 목록"
              aria-haspopup="menu"
              aria-expanded={railChats}
              onClick={() => {
                setRailGroups(false);
                setRailChats((o) => !o);
              }}
            >
              {ICON_CHATS}
              <span>채팅</span>
            </button>
            {railChats && (
              <div className="rail-pop" role="menu" aria-label="최근 채팅">
                <div className="rail-pop-head">최근 채팅</div>
                {sessions.length === 0 ? (
                  <div className="rail-pop-empty">아직 대화가 없어요</div>
                ) : (
                  sessions.slice(0, 12).map((sx) => (
                    <button
                      key={sx.id}
                      className={`rail-pop-item${sx.id === activeId ? " active" : ""}`}
                      type="button"
                      role="menuitem"
                      title={sx.title || "새 채팅"}
                      onClick={() => {
                        setRailChats(false);
                        onOpen(sx.id);
                      }}
                    >
                      {(writingIds || []).includes(sx.id) && <span className="conv-writing" aria-label="응답 작성 중" />}
                      {sx.title || "새 채팅"}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className="side-scroll">
        <div className={`side-section${sectionCollapsed ? " collapsed" : ""}`}>
          <div
            className="section-kicker clickable"
            onClick={() => {
              const on = !sectionCollapsed;
              setSectionCollapsed(on);
              try {
                localStorage.setItem("va_section_collapsed::밸류에이션 에이전트", on ? "1" : "0");
              } catch {
                /* noop */
              }
            }}
          >
            밸류에이션 에이전트
            <span className="kicker-chevron">{sectionCollapsed ? ICON_CHEV_R : ICON_CHEV_D}</span>
          </div>
          {/* 순서: 시장 이슈 · 유사기업 조회 · 금융상품 평가 · DCF 워크스페이스 · 버핏스코어 (사용자 지정) */}
          <button
            className={`side-item${pathname.startsWith("/market") ? " active" : ""}`}
            type="button"
            data-tip="시장 이슈"
            aria-label="오늘의 시장 이슈"
            onClick={() => goSection("/market")}
          >
            {/* Lucide trending-up — 시안(Valuation Agent v2)이 쓴 그 아이콘 */}
            <svg viewBox="0 0 24 24" {...stroke}>
              <path d="M3 17l5.5-6 3.5 3.5L20.5 6" />
              <path d="M15.5 6h5v5" />
            </svg>
            <span>시장 이슈</span>
          </button>
          <button
            className={`side-item${pathname.startsWith("/peer") ? " active" : ""}`}
            type="button"
            data-tip="유사기업 조회"
            aria-label="유사기업(Peer) 조회"
            onClick={() => goSection("/peer")}
          >
            {/* 사람(그룹) 아이콘 — 유사'기업들'의 은유. 저울로 바꿨다가 사용자 지정으로 복원 */}
            <svg viewBox="0 0 24 24" {...stroke}>
              <circle cx="9" cy="7" r="3" />
              <path d="M2 21v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v2" />
              <circle cx="17.5" cy="7.5" r="2.5" />
              <path d="M22 21v-1.5a4 4 0 0 0-3-3.87" />
            </svg>
            <span>유사기업 조회</span>
            {peerDot && <span className={`conv-status ${peerDot}`} aria-label={peerDot === "writing" ? "조회 진행 중" : "조회 완료"} />}
          </button>
          {RCPS_ENABLED && enterprise && (
            <button
              className={`side-item${pathname.startsWith("/rcps") ? " active" : ""}`}
              type="button"
              data-tip="금융상품 평가"
              aria-label="RCPS 금융상품 평가"
              onClick={() => goSection("/rcps")}
            >
              <Layers3 />
              <span>금융상품 평가</span>
              <span className="badge-soon">베타</span>
            </button>
          )}
          {enterprise && (
          <button
            className="side-item"
            type="button"
            data-tip="DCF 워크스페이스"
            aria-label="DCF 프로젝트 워크스페이스"
            onClick={() => goSection("/dcf")}
          >
            <svg viewBox="0 0 24 24" {...stroke}>
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M10 4v4" />
              <path d="M2 8h20" />
              <path d="M6 4v4" />
            </svg>
            <span>DCF 워크스페이스</span>
          </button>
          )}
          <button
            className={`side-item${pathname.startsWith("/buffett") ? " active" : ""}`}
            type="button"
            data-tip="버핏스코어"
            aria-label="버핏스코어 정성 분석"
            onClick={() => goSection("/buffett")}
          >
            {/* Lucide award — 손으로 그린 별은 등급이 아니라 즐겨찾기로 읽힌다 */}
            <svg viewBox="0 0 24 24" {...stroke}>
              <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" />
              <circle cx="12" cy="8" r="6" />
            </svg>
            <span>버핏스코어</span>
            {bfDot && <span className={`conv-status ${bfDot}`} aria-label={bfDot === "writing" ? "분석 진행 중" : "분석 완료"} />}
          </button>
          <button
            className="side-item planned-toggle"
            type="button"
            aria-expanded={plannedOpen}
            onClick={() => {
              const open = !plannedOpen;
              setPlannedOpen(open);
              try {
                localStorage.setItem("va_planned_open", open ? "1" : "0");
              } catch {
                /* noop */
              }
            }}
          >
            {/* chevron 회전은 CSS([aria-expanded]) — 바닐라 lucide chevron-right 과 동일 */}
            <span className="planned-chevron">{ICON_CHEV_R}</span>
            <span>예정 서비스</span>
          </button>
          <div className="planned-group" hidden={!plannedOpen}>
            {/* 리포트 — 정식 메뉴에서 예정 서비스(베타)로 옮김(사용자 요청). 접힌 그룹이라 보고 있을 때는 active 표시 */}
            {REPORT_ENABLED && (
              <button
                className={"side-item" + (pathname.startsWith("/report") ? " active" : "")}
                type="button"
                aria-label="분석 리포트"
                onClick={() => goSection("/report")}
              >
                {/* Lucide file-text — 문서(리포트)의 은유 */}
                <svg viewBox="0 0 24 24" {...stroke}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                  <path d="M8 13h8" />
                  <path d="M8 17h5" />
                </svg>
                <span>리포트</span>
                <span className="badge-soon">베타</span>
              </button>
            )}
            {GRAPH_CHAT_ENABLED && (
              <button
                className={"side-item" + (pathname.startsWith("/graph-chat") ? " active" : "")}
                type="button"
                onClick={() => goSection("/graph-chat")}
              >
                <svg viewBox="0 0 24 24" {...stroke}>
                  <circle cx="5" cy="6" r="2.2" />
                  <circle cx="19" cy="6" r="2.2" />
                  <circle cx="12" cy="18" r="2.2" />
                  <path d="M6.6 7.6 10.6 16" />
                  <path d="M17.4 7.6 13.4 16" />
                  <path d="M7.2 6h9.6" />
                </svg>
                <span>Graph DB 챗</span>
                <span className="badge-soon">베타</span>
              </button>
            )}
            {RE_ENABLED && (
              <button
                className={"side-item" + (pathname.startsWith("/re") ? " active" : "")}
                type="button"
                onClick={() => goSection("/re")}
              >
                <svg viewBox="0 0 24 24" {...stroke}>
                  <path d="M3 21h18" />
                  <path d="M5 21V7l7-4 7 4v14" />
                  <path d="M9 21v-5h6v5" />
                  <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01" />
                </svg>
                <span>부동산 개발·매매사례</span>
                <span className="badge-soon">베타</span>
              </button>
            )}
            <button className="side-item planned" type="button" tabIndex={-1}>
              <svg viewBox="0 0 24 24" {...stroke}>
                <path d="M9 5v4" />
                <rect x="7" y="9" width="4" height="6" rx="1" />
                <path d="M9 15v4" />
                <path d="M17 3v2" />
                <rect x="15" y="5" width="4" height="8" rx="1" />
                <path d="M17 13v3" />
              </svg>
              <span>투자 백테스팅</span>
              <span className="badge-soon">예정</span>
            </button>
            {!RCPS_ENABLED && <>
            <button className="side-item planned" type="button" tabIndex={-1}>
              <svg viewBox="0 0 24 24" {...stroke}>
                <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z" />
                <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                <path d="M12 17.5v-11" />
              </svg>
              <span>RCPS</span>
              <span className="badge-soon">예정</span>
            </button>
            <button className="side-item planned" type="button" tabIndex={-1}>
              <svg viewBox="0 0 24 24" {...stroke}>
                <path d="M21 12c.55 0 1-.45 1-1a10 10 0 0 0-10-10c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1z" />
                <path d="M21.2 15.9A10 10 0 1 1 8 2.8" />
              </svg>
              <span>스톡옵션</span>
              <span className="badge-soon">예정</span>
            </button>
            </>}
          </div>
        </div>

        <div className="side-section side-chats">
          <div id="sessionList">
            {(allGroups.length > 0 || sessions.length > 0) && (
              <>
                {kicker(
                  "__groups__",
                  "그룹",
                  groupsOpen,
                  <button
                    className="kicker-add"
                    type="button"
                    title="새 그룹 만들기"
                    aria-label="새 그룹 만들기"
                    onClick={() => {
                      const g = (window.prompt("새 그룹 이름") || "").trim().slice(0, 30);
                      if (g) store().addGroup(g);
                    }}
                  >
                    {ICON_PLUS}
                  </button>,
                )}
                {groupsOpen && allGroups.length === 0 && (
                  <div className="group-onboard">
                    <b>대화를 그룹으로 정리하세요</b>
                    <p>기업·프로젝트별로 모아두면 이어서 작업하기 쉬워요.</p>
                    <button
                      type="button"
                      onClick={() => {
                        const g = (window.prompt("새 그룹 이름") || "").trim().slice(0, 30);
                        if (g) store().addGroup(g);
                      }}
                    >
                      그룹 만들기
                    </button>
                  </div>
                )}
                {groupsOpen &&
                  allGroups.map((g) => {
                    const open = !collapsed.has(g);
                    const items = grouped.get(g) || [];
                    return (
                      <div key={g} style={{ display: "contents" }}>
                        <div className="conversation group-line">
                          <button
                            className="conversation-open group-row"
                            type="button"
                            aria-expanded={open}
                            onClick={() => toggleCollapsed(g)}
                          >
                            {open ? ICON_FOLDER_OPEN : ICON_FOLDER}
                            <span>{g}</span>
                          </button>
                          <button
                            className="conv-menu-btn"
                            type="button"
                            title="그룹 메뉴"
                            aria-label="그룹 메뉴"
                            aria-haspopup="menu"
                            onClick={(e) => {
                              e.stopPropagation();
                              const anchor = e.currentTarget;
                              setMenu({ kind: "group", name: g, anchor });
                            }}
                          >
                            {ICON_DOTS}
                          </button>
                          <button
                            className="conv-menu-btn"
                            type="button"
                            title="이 그룹에서 새 채팅"
                            aria-label="이 그룹에서 새 채팅"
                            onClick={(e) => {
                              e.stopPropagation();
                              onNewInGroup(g);
                            }}
                          >
                            {ICON_NEWCHAT}
                          </button>
                        </div>
                        {open && items.map((s) => row(s, true))}
                      </div>
                    );
                  })}
              </>
            )}
            {ungrouped.length > 0 && (
              <>
                {kicker("__chats__", "채팅", chatsOpen)}
                {chatsOpen && ungrouped.map((s) => row(s, false))}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="sidebar-foot">
        <button
          className="user-row"
          type="button"
          title={account}
          aria-haspopup="menu"
          aria-expanded={menu?.kind === "user"}
          onClick={(e) => {
            e.stopPropagation();
            const anchor = e.currentTarget;
            setMenu((m) => (m && m.kind === "user" ? null : { kind: "user", anchor }));
          }}
        >
          <span className="user-avatar">{shortName.slice(0, 2).toUpperCase() || "–"}</span>
          <span className="user-name">{shortName}</span>
        </button>
      </div>

      {menuNode}
    </aside>
  );
}
